import "./ProjectTasksBoardBanner.css";

import { useContext, useMemo } from "react";

import { ProjectPhase } from "../../../../../../../constants/project-phase";
import { ProjectPhaseStatus } from "../../../../../../../models";
import { isPastDateByDay } from "../../../../../../../utils";
import { Button, Column, InformationBox, Row } from "../../../../../../../widget";
import { TasksBoardContext } from "../../TasksBoardContext";

export const ProjectTasksBoardBanner = (): JSX.Element | undefined => {
  const {
    workflowSteps,
    projectDates,
    currentPhase,
    projectCachedPhase,
    setShowEditDatesPanel,
    setShowCompletePhaseModal,
  } = useContext(TasksBoardContext);

  const completedPhases = useMemo(() => {
    if (!projectDates) return new Set<ProjectPhase>();

    const completed = new Set<ProjectPhase>();

    if (projectDates.dates.feasibility?.status === ProjectPhaseStatus.COMPLETED) {
      completed.add(ProjectPhase.feasibility);
    }
    if (projectDates.dates.design?.status === ProjectPhaseStatus.COMPLETED) {
      completed.add(ProjectPhase.design);
    }
    if (projectDates.dates.implementation?.status === ProjectPhaseStatus.COMPLETED) {
      completed.add(ProjectPhase.implementation);
    }
    if (projectDates.dates.ongoingMonitoring?.overallStatus === ProjectPhaseStatus.COMPLETED) {
      completed.add(ProjectPhase.ongoingMonitoring);
    }

    return completed;
  }, [projectDates]);

  const pastPhases = useMemo(() => {
    if (!projectDates) return new Set<ProjectPhase>();

    const past = new Set<ProjectPhase>();

    if (projectDates.dates.feasibility?.end !== undefined && isPastDateByDay(projectDates.dates.feasibility.end)) {
      past.add(ProjectPhase.feasibility);
    }
    if (projectDates.dates.design?.end !== undefined && isPastDateByDay(projectDates.dates.design.end)) {
      past.add(ProjectPhase.design);
    }
    if (
      projectDates.dates.implementation?.end !== undefined &&
      isPastDateByDay(projectDates.dates.implementation.end)
    ) {
      past.add(ProjectPhase.implementation);
    }
    if (
      projectDates.dates.ongoingMonitoring.overallEnd !== undefined &&
      isPastDateByDay(projectDates.dates.ongoingMonitoring.overallEnd)
    ) {
      past.add(ProjectPhase.ongoingMonitoring);
    }

    return past;
  }, [projectDates]);

  // Completed phase banner
  if (completedPhases.has(currentPhase)) {
    return (
      <Row spacingV="ml">
        <Column span={12}>
          <InformationBox variant="success">
            <p className="body1">
              <b>You have completed this phase</b>
            </p>
          </InformationBox>
        </Column>
      </Row>
    );
  }

  // Incomplete phase but date is in the past
  if (pastPhases.has(currentPhase)) {
    return (
      <Row spacingV="ml">
        <Column span={12}>
          <InformationBox variant="info">
            <div className="ProjectTasksBoardBannerOverrunningContainer">
              <div className="ProjectTasksBoardBannerOverrunningContent">
                <p className="body1">
                  <b>Your project is now overrunning the current phase range</b>
                </p>
                <p className="body1">
                  The current date is beyond the end date for this phase. To keep your timeline accurate, edit the phase
                  and task deadlines.
                </p>
              </div>
              <div className="ProjectTasksBoardBannerOverrunningButton">
                <Button
                  text="Edit phase dates"
                  textUnderline
                  variant="quinary"
                  fontWeight="light"
                  hasNoPadding
                  onClick={() => setShowEditDatesPanel(true)}
                />
              </div>
            </div>
          </InformationBox>
        </Column>
      </Row>
    );
  }

  // Incomplete phase with all steps completed
  if (currentPhase === projectCachedPhase && workflowSteps?.every((step) => step.isComplete)) {
    return (
      <Row spacingV="ml">
        <Column span={12}>
          <InformationBox variant="secondarySuccess">
            <div className="ProjectTasksBoardBannerOverrunningContainer">
              <div className="ProjectTasksBoardBannerOverrunningContent">
                <p className="body1">
                  <b>All steps in this phase have been completed</b>
                </p>
              </div>
              <div className="ProjectTasksBoardBannerOverrunningButton">
                <Button
                  text="Complete phase"
                  textUnderline
                  variant="quinary"
                  fontWeight="light"
                  hasNoPadding
                  onClick={() => setShowCompletePhaseModal(true)}
                />
              </div>
            </div>
          </InformationBox>
        </Column>
      </Row>
    );
  }

  return undefined;
};
